import { useRef, useEffect } from 'react';
import type { TranscriptEntry } from '@assistmd/types';
import { PanelSkeleton } from '@/components/ui/SkeletonLoader';

interface TranscriptPanelProps {
  entries: TranscriptEntry[];
  interimEntry: TranscriptEntry | null;
  loading?: boolean;
}

const ROLE_BG: Record<string, string> = {
  clinician: 'bg-teal-50 text-teal-700',
  patient: 'bg-cyan-50 text-cyan-700',
  other: 'bg-slate-100 text-slate-600',
};

const ROLE_LABELS: Record<string, string> = {
  clinician: 'Clinician',
  patient: 'Patient',
  other: 'Speaker',
};

/** Border accent per role for the left-edge indicator on each row */
const ROLE_BORDER: Record<string, string> = {
  clinician: 'border-l-teal-400',
  patient: 'border-l-cyan-400',
  other: 'border-l-slate-300',
};

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Compute total word count across all finalized entries */
function totalWords(entries: TranscriptEntry[]): number {
  return entries.reduce(
    (sum, e) => sum + (e.text ? e.text.trim().split(/\s+/).filter(Boolean).length : 0),
    0,
  );
}

/**
 * Live transcript panel with speaker labels and auto-scroll.
 */
export function TranscriptPanel({
  entries,
  interimEntry,
  loading,
}: TranscriptPanelProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new entries with smooth behavior
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [entries.length, interimEntry]);

  if (loading) {
    return <PanelSkeleton />;
  }

  const wordCount = totalWords(entries);

  return (
    <div className="card flex flex-col animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h3 className="section-header">Transcript</h3>

        {/* Metadata pills */}
        <div className="flex items-center gap-3">
          {entries.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="overline">Words</span>
              <span className="font-mono text-xs tabular-nums text-slate-500">
                {wordCount.toLocaleString()}
              </span>
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="overline">Segments</span>
            <span className="font-mono text-xs tabular-nums text-slate-500">
              {entries.length}
            </span>
          </span>
        </div>
      </div>

      {/* ── Scrollable transcript body ── */}
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-y-auto"
        style={{ maxHeight: 'calc(100vh - 340px)', minHeight: '200px' }}
      >
        {entries.length === 0 && !interimEntry ? (
          /* ── Empty state with breathing microphone ── */
          <div className="empty-state py-14">
            <div className="empty-state-icon animate-breathe">
              {/* Microphone icon */}
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                />
              </svg>
            </div>
            <p className="empty-state-title">No transcript yet</p>
            <p className="empty-state-desc">Start recording to capture the conversation</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {entries.map((entry, idx) => (
              <div
                key={entry.id}
                className={`flex gap-3 border-l-2 px-5 py-3 transition-colors duration-200 hover:bg-slate-50/60 ${
                  ROLE_BORDER[entry.role] ?? 'border-l-slate-200'
                } ${idx < 5 ? `animate-stagger-${Math.min(idx + 1, 5) as 1 | 2 | 3 | 4 | 5}` : 'animate-fade-in'}`}
              >
                {/* Left column: timestamp */}
                <div className="flex w-12 shrink-0 pt-0.5">
                  <span className="font-mono text-[0.6875rem] tabular-nums text-slate-400">
                    {formatTimestamp(entry.startMs)}
                  </span>
                </div>

                {/* Right column: speaker + text */}
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    {/* Speaker pill */}
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold leading-none ${
                        ROLE_BG[entry.role] ?? 'bg-slate-500/10 text-slate-400'
                      }`}
                      style={{ border: '1px solid rgba(15, 118, 110, 0.08)' }}
                    >
                      {ROLE_LABELS[entry.role] ?? 'Speaker'}
                    </span>

                    {/* Low-confidence indicator */}
                    {entry.confidence < 0.7 && (
                      <span
                        className="badge-amber flex items-center gap-1 !py-0 !text-2xs"
                        title={`Confidence: ${Math.round(entry.confidence * 100)}%`}
                      >
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                          />
                        </svg>
                        low
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700">
                    {entry.text}
                  </p>
                </div>
              </div>
            ))}

            {/* ── Interim (non-final) entry ── */}
            {interimEntry && (
              <div
                className={`flex gap-3 border-l-2 border-dashed px-5 py-3 ${
                  ROLE_BORDER[interimEntry.role] ?? 'border-l-slate-200'
                } opacity-60`}
              >
                <div className="flex w-12 shrink-0 pt-0.5">
                  <span className="font-mono text-[0.6875rem] tabular-nums text-slate-400">
                    {formatTimestamp(interimEntry.startMs)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold leading-none ${
                        ROLE_BG[interimEntry.role] ?? 'bg-slate-500/10 text-slate-400'
                      }`}
                      style={{ border: '1px solid rgba(15, 118, 110, 0.08)' }}
                    >
                      {ROLE_LABELS[interimEntry.role] ?? 'Speaker'}
                    </span>
                    <span className="shimmer h-2 w-10 rounded-full" />
                  </div>
                  <p className="text-sm italic leading-relaxed text-slate-500">
                    {interimEntry.text}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Auto-scroll indicator pill ── */}
        {entries.length > 4 && (
          <div className="sticky bottom-2 flex justify-center pb-1 pt-2">
            <span className="badge-teal flex items-center gap-1.5 text-2xs shadow-sm">
              <svg
                className="h-3 w-3 animate-pulse"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"
                />
              </svg>
              live
            </span>
          </div>
        )}
      </div>

      {/* ── Bottom gradient divider ── */}
      <div className="divider-gradient" />
    </div>
  );
}
